import { FieldTypes } from '../../../../types/form-types'
import AddressField from '../../wells/address-field'
import LocationFieldEdit from '../../wells/location-field-edit'
import { WellDetailsComponentBase } from './well-details-types'
import LocationFieldView from '../../wells/location-field-view'
import WellDetailsOwnerCardEdit from './well-details-owner-card-edit'
import { getLocalityFromAddressLine } from '../../../services/form-utils'
import { TOOLTIP } from '../../../../constants/tooltips'
import { useWellState } from '../../../context/well-context'
import { Card, FormField, Heading, ReadOnlyField, TextInput } from '@msaf/core-react'

interface WellDetailsLocationEditProps extends WellDetailsComponentBase {
  isExistingOrDecommissioned?: boolean
  isWellGrantedAsLegacy?: boolean
}

export default function WellDetailsLocationEdit({
  isLoading,
  validationErrors,
  fields,
  setValue,
  isSetupRecord,
  isExistingOrDecommissioned,
  isWellGrantedAsLegacy,
}: WellDetailsLocationEditProps) {
  const { wellState } = useWellState()
  return (
    <>
      <Heading isSkeleton={isLoading} level={3}>
        Location
      </Heading>

      {!isExistingOrDecommissioned && (
        <Card>
          <Heading isSkeleton={isLoading} level={4}>
            Proposed
          </Heading>

          {fields.confirmedWellLocation ? (
            <LocationFieldView
              isLoading={isLoading}
              easting={{
                labelId: 'proposedEasting',
                value: `${fields.proposedEasting ?? ''}`,
              }}
              northing={{
                labelId: 'proposedNorthing',
                value: `${fields.proposedNorthing ?? ''}`,
              }}
              latitude={{
                labelId: 'proposedLatitude',
                value: `${fields.proposedLatitude ?? ''}`,
              }}
              longitude={{
                labelId: 'proposedLongitude',
                value: `${fields.proposedLongitude ?? ''}`,
              }}
            />
          ) : (
            <LocationFieldEdit
              validationFieldErrors={validationErrors}
              labelledBy='coordinates'
              isLoading={isLoading}
              easting={{
                fieldId: 'proposedEasting',
                value: fields.proposedEasting ?? '',
                setValue: (value: FieldTypes) => setValue('proposedEasting', value),
              }}
              northing={{
                fieldId: 'proposedNorthing',
                value: fields.proposedNorthing ?? '',
                setValue: (value: FieldTypes) => setValue('proposedNorthing', value),
              }}
              latitude={{
                fieldId: 'proposedLatitude',
                value: fields.proposedLatitude ?? '',
                setValue: (value: FieldTypes) => setValue('proposedLatitude', value),
              }}
              longitude={{
                fieldId: 'proposedLongitude',
                value: fields.proposedLongitude ?? '',
                setValue: (value: FieldTypes) => setValue('proposedLongitude', value),
              }}
            />
          )}
        </Card>
      )}

      {isSetupRecord && isExistingOrDecommissioned && (
        <Card>
          <Heading isSkeleton={isLoading} level={4}>
            Confirmed
          </Heading>
          <LocationFieldEdit
            validationFieldErrors={validationErrors}
            labelledBy='coordinates'
            isLoading={isLoading}
            easting={{
              fieldId: 'actualEasting',
              value: fields.actualEasting ?? '',
              setValue: (value: FieldTypes) => setValue('actualEasting', value),
            }}
            northing={{
              fieldId: 'actualNorthing',
              value: fields.actualNorthing ?? '',
              setValue: (value: FieldTypes) => setValue('actualNorthing', value),
            }}
            latitude={{
              fieldId: 'actualLatitude',
              value: fields.actualLatitude ?? '',
              setValue: (value: FieldTypes) => setValue('actualLatitude', value),
            }}
            longitude={{
              fieldId: 'actualLongitude',
              value: fields.actualLongitude ?? '',
              setValue: (value: FieldTypes) => setValue('actualLongitude', value),
            }}
            setValue={setValue}
            locationAccuracy={fields.locationAccuracy}
            isWellGrantedAsLegacy={isWellGrantedAsLegacy}
            showLocationAccuracy
          />
        </Card>
      )}

      {!isSetupRecord && (
        <Card>
          <Heading isSkeleton={isLoading} level={4}>
            Confirmed
          </Heading>
          <LocationFieldEdit
            isRequired={!isWellGrantedAsLegacy}
            validationFieldErrors={validationErrors}
            labelledBy='coordinates'
            isLoading={isLoading}
            easting={{
              fieldId: 'actualEasting',
              value: fields.actualEasting ?? '',
              setValue: (value: FieldTypes) => setValue('actualEasting', value),
            }}
            northing={{
              fieldId: 'actualNorthing',
              value: fields.actualNorthing ?? '',
              setValue: (value: FieldTypes) => setValue('actualNorthing', value),
            }}
            latitude={{
              fieldId: 'actualLatitude',
              value: fields.actualLatitude ?? '',
              setValue: (value: FieldTypes) => setValue('actualLatitude', value),
            }}
            longitude={{
              fieldId: 'actualLongitude',
              value: fields.actualLongitude ?? '',
              setValue: (value: FieldTypes) => setValue('actualLongitude', value),
            }}
            setValue={setValue}
            locationAccuracy={fields.locationAccuracy}
            isWellGrantedAsLegacy={isWellGrantedAsLegacy}
            showLocationAccuracy
          />
        </Card>
      )}

      {/* TODO: remove/decouple label from address component in core-react module so it can be customised here */}
      {/* TODO: do we need to add a field required message here for AddressField */}
      <AddressField
        isSkeleton={isLoading}
        labelledBy='address' // hard-coded id in address component
        // TODO: fix duplicate address field label/input ids
        addressFields={{
          addressLine1: `${fields.wellAddressLine1 ?? ''}`,
          addressLine2: `${fields.wellAddressLine2 ?? ''}`,
          addressLine3: `${fields.wellAddressLine3 ?? ''}`,
          addressLine4: `${fields.wellAddressLine4 ?? ''}`,
          addressLine5: `${fields.wellAddressLine5 ?? ''}`,
          addressPostcode: `${fields.wellAddressPostcode ?? ''}`,
          isManualEntry: !!fields.isWellAddressEnteredManually,
        }}
        setAddressFields={(addressFields) => {
          setValue('wellAddressLine1', addressFields.addressLine1 ?? '')
          setValue('wellAddressLine2', addressFields.addressLine2 ?? '')
          setValue('wellAddressLine3', addressFields.addressLine3 ?? '')
          setValue('wellAddressLine4', addressFields.addressLine4 ?? '')
          setValue('wellAddressLine5', addressFields.addressLine5 ?? '')
          setValue('wellAddressPostcode', addressFields.addressPostcode ?? '')
          setValue('isWellAddressEnteredManually', !!addressFields.isManualEntry)
          // Sometimes addressLine2 is returned as blank for ESRI, in that case using addressLine3 value
          setValue(
            'wellLocality',
            getLocalityFromAddressLine(addressFields['addressLine2'], addressFields['addressLine3']),
          )
        }}
      />
      {!wellState.isDraft && (
        <FormField
          isSkeleton={isLoading}
          label='Locality'
          htmlFor='locality'
          validationMessages={validationErrors?.wellLocality}
        >
          <TextInput
            isSkeleton={isLoading}
            id='locality'
            value={`${fields.wellLocality ?? ''}`}
            setValue={(value) => setValue('wellLocality', value)}
          />
        </FormField>
      )}

      {!isSetupRecord && (
        <>
          {!wellState.isDraft && (
            <FormField isSkeleton={isLoading} label='Region' htmlFor='region'>
              <ReadOnlyField isSkeleton={isLoading} labelledBy='region' value={`${fields.councilRegionName ?? ''}`} />
            </FormField>
          )}
          <FormField
            toolTipMessage={TOOLTIP.locationDescription}
            isSkeleton={isLoading}
            label='Location description'
            htmlFor='locationDescription'
            validationMessages={validationErrors?.locationDescription}
          >
            <TextInput
              isSkeleton={isLoading}
              id='locationDescription'
              value={`${fields.locationDescription ?? ''}`}
              setValue={(value: string) => setValue('locationDescription', value)}
            />
          </FormField>
        </>
      )}
      <WellDetailsOwnerCardEdit
        fields={fields}
        isLoading={isLoading}
        setValue={setValue}
        validationErrors={validationErrors}
      />
    </>
  )
}
